import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import SoundManager from '../utils/SoundManager';
import { disposeScene } from '../utils/ThreeCleanup';

const LivingSimulationVisualization: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [systemMessages, setSystemMessages] = useState<string[]>([]);
  const [glitchActive, setGlitchActive] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0a0a0a);
    scene.fog = new THREE.Fog(0x0a0a0a, 30, 100);

    const camera = new THREE.PerspectiveCamera(
      60,
      containerRef.current.clientWidth / containerRef.current.clientHeight,
      0.1,
      1000
    );
    camera.position.set(0, 15, 35);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: false
    });
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    containerRef.current.appendChild(renderer.domElement);

    const mainGrid = new THREE.GridHelper(80, 40, 0x00ff88, 0x003322);
    mainGrid.position.y = 0;
    scene.add(mainGrid);

    const verticalGrid = new THREE.GridHelper(60, 30, 0x0088ff, 0x002244);
    verticalGrid.rotation.x = Math.PI / 2;
    verticalGrid.position.z = -20;
    scene.add(verticalGrid);

    const entities: THREE.Mesh[] = [];
    const entityCount = 25;

    for (let i = 0; i < entityCount; i++) {
      const size = 0.5 + Math.random() * 1.5;
      const geometry = new THREE.BoxGeometry(size, size * 2, size);

      const hue = Math.random() * 0.3;
      const material = new THREE.MeshPhongMaterial({
        color: new THREE.Color().setHSL(hue, 0.8, 0.5),
        emissive: new THREE.Color().setHSL(hue, 1, 0.3),
        emissiveIntensity: 0.5,
        shininess: 100,
        transparent: true,
        opacity: 0.9
      });

      const entity = new THREE.Mesh(geometry, material);
      entity.position.x = (Math.random() - 0.5) * 60;
      entity.position.y = size;
      entity.position.z = (Math.random() - 0.5) * 60;
      entity.castShadow = true;
      entity.receiveShadow = true;

      entity.userData = {
        velocity: new THREE.Vector3(
          (Math.random() - 0.5) * 0.1,
          0,
          (Math.random() - 0.5) * 0.1
        ),
        baseY: size,
        phase: Math.random() * Math.PI * 2
      };

      scene.add(entity);
      entities.push(entity);
    }

    const pathLines: THREE.Line[] = [];
    entities.forEach((entity) => {
      const points = [];
      for (let i = 0; i < 50; i++) {
        points.push(entity.position.clone());
      }
      const geometry = new THREE.BufferGeometry().setFromPoints(points);
      const material = new THREE.LineBasicMaterial({
        color: (entity.material as THREE.MeshPhongMaterial).emissive,
        transparent: true,
        opacity: 0.3
      });
      const line = new THREE.Line(geometry, material);
      scene.add(line);
      pathLines.push(line);
      entity.userData.pathLine = line;
      entity.userData.pathPoints = points;
    });

    const coreGeometry = new THREE.SphereGeometry(3, 32, 32);
    const coreMaterial = new THREE.MeshPhongMaterial({
      color: 0x00ffaa,
      emissive: 0x00ffaa,
      emissiveIntensity: 0.8,
      transparent: true,
      opacity: 0.6,
      wireframe: true
    });
    const core = new THREE.Mesh(coreGeometry, coreMaterial);
    core.position.y = 8;
    scene.add(core);

    const coreRings: THREE.Mesh[] = [];
    for (let i = 0; i < 3; i++) {
      const ringGeometry = new THREE.TorusGeometry(4 + i * 2, 0.1, 8, 32);
      const ringMaterial = new THREE.MeshPhongMaterial({
        color: 0x00ffaa,
        emissive: 0x00ffaa,
        emissiveIntensity: 1,
        transparent: true,
        opacity: 0.5
      });
      const ring = new THREE.Mesh(ringGeometry, ringMaterial);
      ring.position.copy(core.position);
      ring.rotation.x = Math.PI / 2;
      scene.add(ring);
      coreRings.push(ring);
    }

    const connectionLines: THREE.Line[] = [];
    entities.forEach((entity) => {
      const points = [entity.position.clone(), core.position.clone()];
      const geometry = new THREE.BufferGeometry().setFromPoints(points);
      const material = new THREE.LineBasicMaterial({
        color: 0x00ffaa,
        transparent: true,
        opacity: 0.1
      });
      const line = new THREE.Line(geometry, material);
      scene.add(line);
      connectionLines.push(line);
      entity.userData.connectionLine = line;
    });

    const particleCount = 1000;
    const particleGeometry = new THREE.BufferGeometry();
    const particlePositions = new Float32Array(particleCount * 3);

    for (let i = 0; i < particleCount; i++) {
      particlePositions[i * 3] = (Math.random() - 0.5) * 100;
      particlePositions[i * 3 + 1] = Math.random() * 50;
      particlePositions[i * 3 + 2] = (Math.random() - 0.5) * 100;
    }

    particleGeometry.setAttribute('position', new THREE.BufferAttribute(particlePositions, 3));

    const particleMaterial = new THREE.PointsMaterial({
      color: 0x00ffaa,
      size: 0.2,
      transparent: true,
      opacity: 0.6,
      blending: THREE.AdditiveBlending
    });

    const particles = new THREE.Points(particleGeometry, particleMaterial);
    scene.add(particles);

    const ambientLight = new THREE.AmbientLight(0x112211, 0.3);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0x00ffaa, 0.8);
    directionalLight.position.set(10, 20, 10);
    directionalLight.castShadow = true;
    directionalLight.shadow.camera.left = -40;
    directionalLight.shadow.camera.right = 40;
    directionalLight.shadow.camera.top = 40;
    directionalLight.shadow.camera.bottom = -40;
    scene.add(directionalLight);

    const coreLight = new THREE.PointLight(0x00ffaa, 2, 50);
    coreLight.position.copy(core.position);
    scene.add(coreLight);

    const messages = [
      'ENTITY SPAWNED: ID_' + Math.floor(Math.random() * 10000),
      'BEHAVIOR PATTERN UPDATED',
      'COLLISION AVOIDANCE ACTIVE',
      'PATHFINDING ALGORITHM ENGAGED',
      'NEURAL NETWORK PROCESSING',
      'DECISION TREE EVALUATED',
      'CONSCIOUSNESS THREAD ACTIVE',
      'ENVIRONMENT SCAN COMPLETE',
      'INTERACTION PROTOCOL LOADED',
      'SYSTEM COHERENCE: OPTIMAL'
    ];

    let messageIndex = 0;
    const messageInterval = setInterval(() => {
      const message = messages[Math.floor(Math.random() * messages.length)];
      setSystemMessages(prev => {
        const newMessages = [message, ...prev.slice(0, 5)];
        return newMessages;
      });
      messageIndex++;
      if (Math.random() > 0.7) {
        SoundManager.playSound('keypress');
      }
    }, 2000);

    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();
    let hoveredEntity: THREE.Mesh | null = null;

    const onMouseMove = (event: MouseEvent) => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / containerRef.current.clientWidth) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / containerRef.current.clientHeight) * 2 + 1;

      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObjects(entities);

      if (hoveredEntity && hoveredEntity.material instanceof THREE.MeshPhongMaterial) {
        hoveredEntity.material.emissiveIntensity = 0.5;
        hoveredEntity.scale.set(1, 1, 1);
      }

      if (intersects.length > 0) {
        hoveredEntity = intersects[0].object as THREE.Mesh;
        if (hoveredEntity.material instanceof THREE.MeshPhongMaterial) {
          hoveredEntity.material.emissiveIntensity = 1.5;
          hoveredEntity.scale.set(1.3, 1.3, 1.3);
        }
      } else {
        hoveredEntity = null;
      }
    };

    const onClick = () => {
      if (hoveredEntity) {
        SoundManager.playSound('command');
        setGlitchActive(true);
        setTimeout(() => setGlitchActive(false), 200);

        hoveredEntity.userData.velocity.x *= -1.5;
        hoveredEntity.userData.velocity.z *= -1.5;
      }
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('click', onClick);

    let time = 0;
    let animationFrameId = 0;
    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);

      time += 0.016;

      core.rotation.y += 0.01;
      core.rotation.x = Math.sin(time * 0.5) * 0.2;

      coreRings.forEach((ring, index) => {
        ring.rotation.z += 0.01 * (index + 1);
        ring.scale.setScalar(1 + Math.sin(time * 2 + index) * 0.1);
      });

      entities.forEach((entity, index) => {
        const velocity = entity.userData.velocity;
        entity.position.x += velocity.x;
        entity.position.z += velocity.z;

        if (Math.abs(entity.position.x) > 35) velocity.x *= -1;
        if (Math.abs(entity.position.z) > 35) velocity.z *= -1;

        entity.position.y = entity.userData.baseY + Math.sin(time * 2 + entity.userData.phase) * 0.5;

        entity.rotation.y += 0.02;
        entity.rotation.x = Math.sin(time + index) * 0.3;

        entities.forEach((other, otherIndex) => {
          if (index !== otherIndex) {
            const distance = entity.position.distanceTo(other.position);
            if (distance < 3) {
              const direction = entity.position.clone().sub(other.position).normalize();
              velocity.x += direction.x * 0.01;
              velocity.z += direction.z * 0.01;
            }
          }
        });

        velocity.multiplyScalar(0.98);

        const pathPoints = entity.userData.pathPoints;
        pathPoints.shift();
        pathPoints.push(entity.position.clone());
        const pathLine = entity.userData.pathLine;
        pathLine.geometry.setFromPoints(pathPoints);

        const connectionLine = entity.userData.connectionLine;
        const positions = connectionLine.geometry.attributes.position.array as Float32Array;
        positions[0] = entity.position.x;
        positions[1] = entity.position.y;
        positions[2] = entity.position.z;
        positions[3] = core.position.x;
        positions[4] = core.position.y;
        positions[5] = core.position.z;
        connectionLine.geometry.attributes.position.needsUpdate = true;

        const distToCore = entity.position.distanceTo(core.position);
        if (connectionLine.material instanceof THREE.LineBasicMaterial) {
          connectionLine.material.opacity = Math.max(0.05, 0.3 - distToCore / 100);
        }
      });

      const positions = particles.geometry.attributes.position.array as Float32Array;
      for (let i = 0; i < particleCount; i++) {
        positions[i * 3 + 1] += 0.05;
        if (positions[i * 3 + 1] > 50) {
          positions[i * 3 + 1] = 0;
        }
      }
      particles.geometry.attributes.position.needsUpdate = true;

      camera.position.x = Math.sin(time * 0.1) * 5;
      camera.position.z = 35 + Math.cos(time * 0.1) * 5;
      camera.lookAt(0, 5, 0);

      renderer.render(scene, camera);
    };

    animate();

    const handleResize = () => {
      if (!containerRef.current) return;

      const width = containerRef.current.clientWidth;
      const height = containerRef.current.clientHeight;

      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('click', onClick);
      window.removeEventListener('resize', handleResize);
      clearInterval(messageInterval);

      if (containerRef.current && containerRef.current.contains(renderer.domElement)) {
        containerRef.current.removeChild(renderer.domElement);
      }
      disposeScene(scene, renderer);
    };
  }, []);

  return (
    <div className="w-full h-full flex flex-col relative">
      <div
        className={`relative flex-grow ${glitchActive ? 'animate-pulse' : ''}`}
        ref={containerRef}
        style={{
          filter: glitchActive ? 'hue-rotate(90deg) saturate(2)' : 'none',
          transition: 'filter 0.1s'
        }}
      >
        <div className="absolute top-4 left-4 bg-black bg-opacity-90 border border-green-500 p-4 rounded font-mono max-w-md">
          <div className="text-xs text-green-400">
            <div className="text-sm font-bold mb-3 text-green-300 flex items-center gap-2">
              <span className="inline-block w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
              SIMULATION LOG
            </div>
            {systemMessages.map((message, index) => (
              <div
                key={index}
                className="mb-1.5 text-green-500 border-l-2 border-green-700 pl-2"
                style={{ opacity: 1 - (index * 0.15) }}
              >
                <span className="text-green-600">[{new Date().toLocaleTimeString()}]</span> {message}
              </div>
            ))}
          </div>
        </div>

        <div className="absolute top-4 right-4 bg-black bg-opacity-90 border border-cyan-500 p-4 rounded font-mono">
          <div className="text-xs text-cyan-400">
            <div className="text-sm font-bold mb-2 text-cyan-300">SYSTEM STATUS</div>
            <div className="space-y-1">
              <div>Entities: <span className="text-white font-bold">25</span></div>
              <div>Core: <span className="text-green-400 font-bold">STABLE</span></div>
              <div>Paths: <span className="text-cyan-300 font-bold">ACTIVE</span></div>
              <div>Neural Load: <span className="text-yellow-400 font-bold">67%</span></div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-4 left-4 bg-black bg-opacity-90 border border-yellow-500 p-3 rounded font-mono">
          <div className="text-xs text-yellow-400">
            <div className="font-bold mb-1.5 text-yellow-300">INTERACTIONS</div>
            <div className="space-y-1">
              <div>→ Hover entities to highlight</div>
              <div>→ Click to alter behavior</div>
              <div>→ Watch autonomous patterns</div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-4 right-4 bg-black bg-opacity-90 border border-purple-500 p-3 rounded font-mono">
          <div className="text-xs text-purple-400">
            <div className="text-sm font-bold mb-2 text-purple-300">LIVING SIMULATION</div>
            <div className="text-[10px] leading-relaxed text-purple-400">
              Each entity is autonomous, following<br/>
              emergent behavior patterns and<br/>
              collision avoidance algorithms.<br/>
              The core coordinates their dance.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LivingSimulationVisualization;
